import { createSelector } from 'reselect';

import { getFilterIdFromProps, getSliderValueFromProps } from '../../../selectors/_propsGetters_';
import { makeMatchBetsArraySelector } from '../../../selectors/match.selector';
import { selectMyMatchFutureEntitiesBets } from '../../../selectors/mymatch/mymatchPrimary.selector';
import { type BetState } from '../../../states/entities/bets';
import { type MyMatchFutureEntitiesBetState, type MyMatchBetStatus, MyMatchBetStatuses } from '../../../states/mymatch';
import { MarketId, MarketIds } from '../../../types/market';
import { getBetTypeFromFilterId, getMarketIdFromFilterId, getSBVFromFilterId } from '../../../types/matchBetFilters';
import { type SelectorMaker } from '../../../types/selectors';
import { createShallowEqualSelector } from '../../../utils/reselect';
import { getBetSliderValue } from '../utils';

export const marketIdWithSpecifiers: Array<MarketId> = [
  MarketIds.MARKET_ID_MULTIPLE_GOALSCORER,
  MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_1_HT,
  MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_2_HT,
  MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_10MINUTES,
  MarketIds.MARKET_ID_SOCCER_SCORER_PERIOD_15MINUTES,
  MarketIds.MARKET_ID_SOCCER_MULTI_SCORER_OVERTIME,
];

export const makeBetTypeFromFilterIdSelector: SelectorMaker<number | null> = () =>
  createSelector([getFilterIdFromProps], (filterId) => (filterId ? getBetTypeFromFilterId(filterId) : null));

export const makeMarketIdFromFilterIdSelector: SelectorMaker<number | null> = () =>
  createSelector([getFilterIdFromProps], (filterId) => (filterId ? getMarketIdFromFilterId(filterId) : null));

export const makeSBVFromFilterIdSelector: SelectorMaker<number | string | null> = () =>
  createSelector([getFilterIdFromProps], (filterId) => (filterId ? getSBVFromFilterId(filterId) : null));

export const makeMatchBetsByMarketIdSelector: SelectorMaker<Array<BetState>> = () =>
  createShallowEqualSelector(
    [makeMatchBetsArraySelector(), makeMarketIdFromFilterIdSelector(), makeBetTypeFromFilterIdSelector()],
    (bets, marketId, betType) => {
      if (betType) {
        return bets.filter((bet) => betType === bet.betType);
      }
      return bets.filter((bet) => marketId === bet.marketId);
    },
  );

export const makeMatchBetsBySliderValueSelector: SelectorMaker<Array<BetState>> = () =>
  createShallowEqualSelector([makeMatchBetsByMarketIdSelector(), getSliderValueFromProps], (bets, sliderValue) => {
    if (sliderValue) {
      return bets.filter((bet) => getBetSliderValue(bet, 'total') === sliderValue);
    }
    return bets;
  });

export const makeMatchBetByMarketIdSelector: SelectorMaker<BetState | undefined> = () =>
  createSelector([makeMatchBetsBySliderValueSelector(), makeSBVFromFilterIdSelector()], (bets, specialBetValue) =>
    bets.find((bet) => (specialBetValue && bet.specialBetValue === specialBetValue) || !specialBetValue),
  );

export const makeMatchBetIdByMarketIdSelector: SelectorMaker<number | undefined> = () =>
  createSelector(makeMatchBetByMarketIdSelector(), (bet) => bet?.betId);

export const makeMyMatchFutureEntitiesBetByMarketIdSelector: SelectorMaker<
  MyMatchFutureEntitiesBetState | undefined
> = () =>
  createSelector([selectMyMatchFutureEntitiesBets, makeMatchBetIdByMarketIdSelector()], (futureBets, betId) => {
    if (betId) {
      return futureBets[betId];
    }
  });

export const makeBetMMStatusByMarketIdSelector: SelectorMaker<MyMatchBetStatus> = () =>
  createSelector(
    [makeMyMatchFutureEntitiesBetByMarketIdSelector()],
    (bet) => bet?.mmStatus ?? MyMatchBetStatuses.INACTIVE,
  );

export const makeBetMultichoiceMMStatusByMarketIdSelector: SelectorMaker<MyMatchBetStatus> = () =>
  createSelector(
    [makeMyMatchFutureEntitiesBetByMarketIdSelector()],
    (bet) => bet?.mmMultichoiceStatus ?? MyMatchBetStatuses.INACTIVE,
  );
